import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Button } from "@mui/material";

const normalizeLink = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.toString();
  } catch (_error) {
    return "";
  }
};

export default function SimpleRichTextEditor({
  value,
  onChange,
  disabled = false,
  minHeight = 220,
}) {
  const editorRef = useRef(null);

  useEffect(() => {
    if (!editorRef.current) return;
    const nextValue = value || "";
    if (editorRef.current.innerHTML !== nextValue) {
      editorRef.current.innerHTML = nextValue;
    }
  }, [value]);

  const apply = (command, commandValue = null) => {
    if (disabled || !editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(command, false, commandValue);
    onChange(editorRef.current.innerHTML || "");
  };

  const insertLink = () => {
    if (disabled || !editorRef.current) return;
    const urlInput = window.prompt("Link eingeben (https://...)");
    const href = normalizeLink(urlInput);
    if (!href) return;
    apply("createLink", href);
  };

  const handleInput = () => {
    if (disabled || !editorRef.current) return;
    onChange(editorRef.current.innerHTML || "");
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          p: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
          backgroundColor: "background.default",
        }}
      >
        <Button size="small" variant="outlined" onClick={() => apply("formatBlock", "H2")} disabled={disabled}>
          H2
        </Button>
        <Button size="small" variant="outlined" onClick={() => apply("formatBlock", "H3")} disabled={disabled}>
          H3
        </Button>
        <Button size="small" variant="outlined" onClick={() => apply("bold")} disabled={disabled}>
          Fett
        </Button>
        <Button size="small" variant="outlined" onClick={() => apply("italic")} disabled={disabled}>
          Kursiv
        </Button>
        <Button size="small" variant="outlined" onClick={() => apply("insertUnorderedList")} disabled={disabled}>
          Liste
        </Button>
        <Button size="small" variant="outlined" onClick={() => apply("insertOrderedList")} disabled={disabled}>
          Nummeriert
        </Button>
        <Button size="small" variant="outlined" onClick={insertLink} disabled={disabled}>
          Link
        </Button>
        <Button size="small" variant="outlined" onClick={() => apply("removeFormat")} disabled={disabled}>
          Format löschen
        </Button>
      </Box>
      <Box
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={handleInput}
        sx={{
          p: 2,
          minHeight,
          outline: "none",
          backgroundColor: disabled ? "action.hover" : "background.paper",
        }}
      />
    </Box>
  );
}

SimpleRichTextEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  minHeight: PropTypes.number,
};
